<script setup lang="ts">
import type { GameVehicleSummary, GameVehicleType } from '~~/types/gamedata';

const props = defineProps<{
  vehicles: GameVehicleSummary[];
}>();

interface FilterOption {
  label: string;
  value: string;
  chipLabel: string;
}

type FilterKind = 'size' | 'career' | 'manufacturer' | 'vehicleType' | 'productionStatus';

interface ActiveFilter {
  key: string;
  label: string;
  kind: FilterKind;
  value: string;
}

const search = defineModel<string>('search', { default: '' });
const sizes = defineModel<string[]>('sizes', { default: () => [] });
const careers = defineModel<string[]>('careers', { default: () => [] });
const manufacturers = defineModel<string[]>('manufacturers', { default: () => [] });
const vehicleTypes = defineModel<GameVehicleType[]>('vehicleTypes', { default: () => [] });
const productionStatuses = defineModel<string[]>('productionStatuses', { default: () => [] });

/** Größen und Kategorien samt Trefferzahl aus der Wiki-Filter-Definition. */
const { data: filters } = useVehicleFilters();

const UNKNOWN_MANUFACTURER = '__unknown__';

function manufacturerValue(vehicle: GameVehicleSummary): string {
  const name = vehicle.manufacturer.name?.trim();
  return !name || name.toLowerCase() === 'unknown' ? UNKNOWN_MANUFACTURER : name;
}

function sortByLabel(options: FilterOption[]): FilterOption[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, 'de'));
}

const sizeOptions = computed<FilterOption[]>(() => {
  // `resolveSize()` kappt Schiffsgrößen auf 1–6. Bodenfahrzeuge werden über
  // den separaten Typfilter ausgewählt und haben intern die Größe `v`.
  const options = (filters.value.size ?? [])
    .filter((option) => typeof option.value === 'number' && option.value >= 1 && option.value <= 6)
    .map((option) => ({
      label: `${getSizeLetter(String(option.value))} (${option.count})`,
      chipLabel: getSizeLetter(String(option.value)),
      value: String(option.value),
    }));
  return options.sort((a, b) => Number(a.value) - Number(b.value));
});

const careerOptions = computed<FilterOption[]>(() => sortByLabel(
  (filters.value.career ?? [])
    .filter((option): option is typeof option & { value: string } => typeof option.value === 'string')
    .map((option) => ({
      label: `${option.label} (${option.count})`,
      chipLabel: option.label,
      value: option.value,
    })),
));

// Aus dem tatsächlich dargestellten Index aufgebaut. Die Wiki-Facetten
// enthalten teilweise andere Namen als das CMS (z.B. Banu) und würden daher
// auswählbare Optionen ohne Treffer erzeugen.
const manufacturerOptions = computed<FilterOption[]>(() => {
  const counts = new Map<string, number>();
  for (const vehicle of props.vehicles) {
    const value = manufacturerValue(vehicle);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }

  return sortByLabel([...counts].map(([value, count]) => {
    const chipLabel = value === UNKNOWN_MANUFACTURER ? 'Unknown' : value;
    return { label: `${chipLabel} (${count})`, chipLabel, value };
  }));
});

const vehicleTypeLabels: Record<GameVehicleType, string> = {
  ship: 'Ship',
  gravlev: 'Gravlev',
  ground: 'Ground',
  powersuit: 'Powersuit',
};

const vehicleTypeOptions = computed<FilterOption[]>(() => {
  const counts: Record<GameVehicleType, number> = {
    ship: 0,
    gravlev: 0,
    ground: 0,
    powersuit: 0,
  };
  for (const vehicle of props.vehicles) counts[vehicle.vehicleType] += 1;

  return (Object.keys(vehicleTypeLabels) as GameVehicleType[]).map((value) => ({
    label: `${vehicleTypeLabels[value]} (${counts[value]})`,
    chipLabel: vehicleTypeLabels[value],
    value,
  }));
});

const productionStatusOptions = computed<FilterOption[]>(() => {
  const counts = { 'flight-ready': 0, 'in-concept': 0 };
  for (const vehicle of props.vehicles) {
    if (vehicle.productionStatusKey === 'in-concept' || vehicle.isConcept) counts['in-concept'] += 1;
    else if (vehicle.productionStatusKey === 'flight-ready') counts['flight-ready'] += 1;
  }

  return [
    { label: `Flugbereit (${counts['flight-ready']})`, chipLabel: 'Flugbereit', value: 'flight-ready' },
    { label: `Konzept (${counts['in-concept']})`, chipLabel: 'Konzept', value: 'in-concept' },
  ];
});

function optionLabel(options: FilterOption[], value: string): string {
  return options.find((option) => option.value === value)?.chipLabel ?? value;
}

const activeFilters = computed<ActiveFilter[]>(() => [
  ...sizes.value.map((value) => ({
    key: `size:${value}`,
    label: `Größe: ${optionLabel(sizeOptions.value, value)}`,
    kind: 'size' as const,
    value,
  })),
  ...careers.value.map((value) => ({
    key: `career:${value}`,
    label: `Kategorie: ${optionLabel(careerOptions.value, value)}`,
    kind: 'career' as const,
    value,
  })),
  ...manufacturers.value.map((value) => ({
    key: `manufacturer:${value}`,
    label: `Hersteller: ${optionLabel(manufacturerOptions.value, value)}`,
    kind: 'manufacturer' as const,
    value,
  })),
  ...vehicleTypes.value.map((value) => ({
    key: `vehicleType:${value}`,
    label: `Typ: ${optionLabel(vehicleTypeOptions.value, value)}`,
    kind: 'vehicleType' as const,
    value,
  })),
  ...productionStatuses.value.map((value) => ({
    key: `productionStatus:${value}`,
    label: `Status: ${optionLabel(productionStatusOptions.value, value)}`,
    kind: 'productionStatus' as const,
    value,
  })),
]);

function removeFilter(filter: ActiveFilter): void {
  if (filter.kind === 'size') sizes.value = sizes.value.filter((value) => value !== filter.value);
  if (filter.kind === 'career') careers.value = careers.value.filter((value) => value !== filter.value);
  if (filter.kind === 'manufacturer') manufacturers.value = manufacturers.value.filter((value) => value !== filter.value);
  if (filter.kind === 'vehicleType') {
    vehicleTypes.value = vehicleTypes.value.filter((value) => value !== filter.value);
  }
  if (filter.kind === 'productionStatus') {
    productionStatuses.value = productionStatuses.value.filter((value) => value !== filter.value);
  }
}

function clearFilters(): void {
  sizes.value = [];
  careers.value = [];
  manufacturers.value = [];
  vehicleTypes.value = [];
  productionStatuses.value = [];
}
</script>

<template>
  <div class="space-y-3">
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <UInput
        v-model="search"
        icon="i-lucide-search"
        placeholder="Fahrzeug suchen…"
        size="lg"
      />
      <USelectMenu
        v-model="sizes"
        multiple
        :items="sizeOptions"
        value-key="value"
        label-key="label"
        placeholder="Alle Größen"
        size="lg"
      />
      <USelectMenu
        v-model="careers"
        multiple
        searchable
        :items="careerOptions"
        value-key="value"
        label-key="label"
        placeholder="Alle Kategorien"
        size="lg"
      />
      <USelectMenu
        v-model="manufacturers"
        multiple
        searchable
        :items="manufacturerOptions"
        value-key="value"
        label-key="label"
        placeholder="Alle Hersteller"
        size="lg"
      />
      <USelectMenu
        v-model="vehicleTypes"
        multiple
        :items="vehicleTypeOptions"
        value-key="value"
        label-key="label"
        placeholder="Alle Typen"
        size="lg"
      />
      <USelectMenu
        v-model="productionStatuses"
        multiple
        :items="productionStatusOptions"
        value-key="value"
        label-key="label"
        placeholder="Produktionsstatus"
        size="lg"
      />
    </div>

    <div v-if="activeFilters.length" class="flex flex-wrap items-center gap-2" aria-label="Aktive Filter">
      <UButton
        v-for="filter in activeFilters"
        :key="filter.key"
        :label="filter.label"
        trailing-icon="i-lucide-x"
        color="primary"
        variant="soft"
        size="xs"
        class="rounded-full"
        :aria-label="`${filter.label} entfernen`"
        @click="removeFilter(filter)"
      />
      <UButton
        v-if="activeFilters.length > 1"
        label="Alle Filter entfernen"
        icon="i-lucide-filter-x"
        color="secondary"
        variant="link"
        size="xs"
        @click="clearFilters"
      />
    </div>
  </div>
</template>
